import { useRef } from "react";
import { useController, Validate } from "react-hook-form";
import { FieldPath, FieldValues } from "react-hook-form/dist/types";

import { Box, FormTextInput } from "@seneca/senekit";

import { KeyboardShortcut } from "seneca-common/utils/components/keyboard-shortcuts";

import { FormValue } from "./common";

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  defaultValue?: FormValue;
  autoFocus?: boolean;
  validateObj?: Validate<any> | Record<string, Validate<any>>;
  type?: "text" | "email" | "password";
  onEnterKeyPress?: () => void;
};

export default function ControlledTextInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  label,
  placeholder,
  isDisabled,
  autoFocus,
  defaultValue,
  validateObj,
  type = "text",
  onEnterKeyPress
}: Props<TFieldValues, TName>) {
  const ref = useRef<HTMLElement>();
  const { field, fieldState } = useController<TFieldValues>({
    name,
    defaultValue: defaultValue || ("" as FormValue),
    rules: { validate: validateObj }
  });
  const { ref: _, ...fieldWithoutRef } = field;

  function handleEnterKeyPress(event: KeyboardEvent) {
    event.preventDefault();
    onEnterKeyPress?.();
  }

  const inputElement = (
    <FormTextInput
      type={type}
      {...fieldWithoutRef}
      name={name}
      autoFocus={autoFocus}
      label={label}
      placeholder={placeholder}
      disabled={isDisabled}
      error={(<>{fieldState.error?.message || fieldState.error}</>) as any}
      showError={!!fieldState?.error}
    />
  );

  return (
    <Box ref={ref}>
      {onEnterKeyPress ? (
        <KeyboardShortcut
          shortcut="enter"
          handler={handleEnterKeyPress}
          bindTo={ref}
        >
          {inputElement}
        </KeyboardShortcut>
      ) : (
        inputElement
      )}
    </Box>
  );
}
